import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { ApiError, apiFetchBlob } from '../../lib/api-client';

interface ProtectedPolicyPdfProps {
  endpoint: string;
  title: string;
  requireEndConfirmation: boolean;
  reachedEnd: boolean;
  onReachedEnd: () => void;
}

function errorText(error: unknown): string {
  return error instanceof ApiError ? error.message : 'โหลด PDF ไม่สำเร็จ กรุณาลองใหม่';
}

/**
 * Native PDF viewers เก็บ scroll ไว้ภายใน plugin/iframe ซึ่งหน้าเว็บอ่านไม่ได้อย่าง
 * น่าเชื่อถือ จึงไม่ใช้ scrollTop ของกรอบนอกเป็นหลักฐานปลอม. ไฟล์ถูก fetch ด้วย JWT
 * ก่อน แล้วผู้ใช้ยืนยันที่ท้ายกรอบว่าอ่านถึงหน้าสุดท้ายจริง; เอกสารสั้นจึงไม่ติดด่าน.
 */
export function ProtectedPolicyPdf({
  endpoint,
  title,
  requireEndConfirmation,
  reachedEnd,
  onReachedEnd,
}: ProtectedPolicyPdfProps) {
  const [attempt, setAttempt] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [viewerFailed, setViewerFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let localUrl: string | null = null;
    setLoading(true);
    setLoadError(null);
    setViewerFailed(false);
    setBlobUrl(null);

    void apiFetchBlob(endpoint)
      .then(async (blob) => {
        const header = await blob.slice(0, 1024).text();
        if (!header.includes('%PDF-')) throw new Error('ไฟล์ที่ได้รับไม่ใช่ PDF');
        if (cancelled) return;
        localUrl = URL.createObjectURL(blob);
        setBlobUrl(localUrl);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(errorText(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [endpoint, attempt]);

  if (loading) {
    return <div className="h-64 flex items-center justify-center text-sm text-gray-500">กำลังโหลด PDF ที่ได้รับอนุญาต…</div>;
  }

  if (loadError || !blobUrl) {
    return (
      <div className="min-h-64 flex flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
        <AlertTriangle className="w-8 h-8 text-amber-600" />
        <p className="font-medium">เปิดไฟล์ PDF ไม่สำเร็จ</p>
        <p className="text-sm text-gray-600">{loadError ?? 'ไม่พบไฟล์ PDF'}</p>
        <button type="button" className="btn-secondary min-h-11" onClick={() => setAttempt((value) => value + 1)}>
          <RefreshCw className="w-4 h-4" /> ลองโหลดใหม่
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 p-2 space-y-2">
      {viewerFailed ? (
        <div className="min-h-48 flex flex-col items-center justify-center gap-3 p-5 text-center bg-white" role="alert">
          <AlertTriangle className="w-8 h-8 text-amber-600" />
          <p className="font-medium">ตัวอ่าน PDF ในเครื่องนี้แสดงผลไม่ได้</p>
          <p className="text-sm text-gray-600">เปิดไฟล์ในหน้าต่างใหม่เพื่ออ่าน แล้วกลับมายืนยันด้านล่าง</p>
          <button type="button" className="btn-secondary min-h-11" onClick={() => setAttempt((value) => value + 1)}>
            <RefreshCw className="w-4 h-4" /> ลองแสดงในหน้านี้ใหม่
          </button>
        </div>
      ) : (
        <object
          data={`${blobUrl}#view=FitH`}
          type="application/pdf"
          title={`PDF ${title}`}
          className="w-full h-[72vh] min-h-[28rem] bg-white border-0"
          onError={() => setViewerFailed(true)}
        >
          <div className="min-h-48 flex flex-col items-center justify-center gap-3 p-5 text-center bg-white" role="alert">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
            <p className="font-medium">อุปกรณ์นี้ไม่รองรับการแสดง PDF ในหน้า</p>
            <button type="button" className="btn-secondary min-h-11" onClick={() => setAttempt((value) => value + 1)}>
              <RefreshCw className="w-4 h-4" /> ลองโหลดใหม่
            </button>
          </div>
        </object>
      )}

      <div className="bg-white p-4 border border-gray-200 rounded-lg space-y-3">
        <a href={blobUrl} target="_blank" rel="noreferrer" className="btn-secondary min-h-11">
          <ExternalLink className="w-4 h-4" /> เปิด PDF แยกหน้าต่าง
        </a>
        {requireEndConfirmation && (
          <label className="flex items-start gap-3 min-h-11 text-sm">
            <input
              type="checkbox"
              className="w-5 h-5 mt-0.5 shrink-0"
              checked={reachedEnd}
              onChange={(event) => { if (event.target.checked) onReachedEnd(); }}
            />
            <span>ข้าพเจ้าเลื่อนอ่าน PDF ฉบับนี้จนถึงหน้าสุดท้ายแล้ว</span>
          </label>
        )}
      </div>
    </div>
  );
}
